import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { toast } from 'sonner';
import { useTheme } from '../../contexts/ThemeContext';
import { 
  Wrench, Car, ClipboardList, FileText, TrendingUp, Clock, 
  Plus, ChevronRight, AlertCircle, CheckCircle2, Truck, CalendarCheck,
  CalendarX, Package, IndianRupee, Users, ArrowUpRight, ArrowDownRight,
  Calendar, Hammer, PaintBucket
} from 'lucide-react';
import { format, startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, subDays } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GarageDashboard = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [garage, setGarage] = useState(null);
  
  // Dashboard data states
  const [bookingStats, setBookingStats] = useState({
    walkinsToday: 0,
    pickupsToday: 0,
    missedYesterday: 0,
    missedToday: 0,
    walkinsTomorrow: 0,
    pickupsTomorrow: 0,
    todayTotal: 0,
    convertedToday: 0,
    driverPending: 0
  });
  
  const [jobStats, setJobStats] = useState({
    carsInGarage: 0,
    waitingEstimate: 0,
    waitingApproval: 0,
    waitingParts: 0,
    readyDelivery: 0,
    delivered: 0
  });
  
  const [revenueFilter, setRevenueFilter] = useState('today');
  const [revenueStats, setRevenueStats] = useState({
    total: 0,
    parts: 0,
    labor: 0,
    bodyShopLabor: 0
  });

  useEffect(() => {
    const token = localStorage.getItem('garage_token');
    const profile = localStorage.getItem('garage_profile');
    
    if (!token) {
      navigate('/garage/login');
      return;
    }
    
    if (profile) {
      setGarage(JSON.parse(profile));
    }
    
    fetchDashboardData();
  }, [navigate]);

  useEffect(() => {
    fetchRevenueData();
  }, [revenueFilter]);

  const fetchDashboardData = async () => {
    const token = localStorage.getItem('garage_token');
    const headers = { 'Authorization': `Bearer ${token}` };
    const garageId = getGarageId();
    
    if (!garageId) {
      console.error('No garage ID found');
      setLoading(false);
      return;
    }

    try {
      // Fetch bookings
      const bookingsRes = await fetch(`${API_URL}/api/garage/bookings?garage_id=${garageId}`, { headers });
      const bookingsData = await bookingsRes.json();
      
      // Fetch jobs
      const jobsRes = await fetch(`${API_URL}/api/garage/jobs/?limit=1000`, { headers });
      const jobsData = await jobsRes.json();
      
      if (bookingsData.success && bookingsData.bookings) {
        processBookingStats(bookingsData.bookings);
      }
      
      if (jobsData.success && jobsData.jobs) {
        processJobStats(jobsData.jobs);
      }
      
      await fetchRevenueData();
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getGarageId = () => {
    const token = localStorage.getItem('garage_token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.garage_id;
      } catch (e) {
        console.error('Error parsing token:', e);
      }
    }
    // Fallback to profile
    const profile = localStorage.getItem('garage_profile');
    if (profile) {
      try {
        return JSON.parse(profile).id;
      } catch (e) {}
    }
    return null;
  };

  const processBookingStats = (bookings) => {
    const today = new Date();
    const yesterday = subDays(today, 1);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    const todayStr = format(today, 'yyyy-MM-dd');
    const yesterdayStr = format(yesterday, 'yyyy-MM-dd');
    const tomorrowStr = format(tomorrow, 'yyyy-MM-dd');
    
    let walkinsToday = 0, pickupsToday = 0, missedYesterday = 0, missedToday = 0;
    let walkinsTomorrow = 0, pickupsTomorrow = 0;
    let todayTotal = 0, convertedToday = 0;
    let driverPending = 0; // Bookings needing pickup but no driver assigned

    bookings.forEach(b => {
      const bookingDate = b.preferred_date;
      const hasJob = !!b.job_id;
      const needsPickup = b.requires_pickup;
      const hasDriver = !!b.driver_id;
      
      // Driver assignment pending - needs pickup but no driver assigned, and not yet converted to job
      if (needsPickup && !hasDriver && !hasJob && b.status !== 'cancelled') {
        driverPending++;
      }
      
      // Tomorrow's bookings
      if (bookingDate === tomorrowStr) {
        if (needsPickup) pickupsTomorrow++;
        else walkinsTomorrow++;
      }
      
      // Today's bookings
      if (bookingDate === todayStr) {
        todayTotal++;
        if (hasJob) convertedToday++;
        if (needsPickup) pickupsToday++;
        else walkinsToday++;
        
        // Missed today (no job card and time passed)
        if (!hasJob && isPastTime(b.preferred_time)) {
          missedToday++;
        }
      }
      
      // Yesterday's missed (no job card)
      if (bookingDate === yesterdayStr && !hasJob) {
        missedYesterday++;
      }
    });

    setBookingStats({
      walkinsToday,
      pickupsToday,
      missedYesterday,
      missedToday,
      walkinsTomorrow,
      pickupsTomorrow,
      todayTotal,
      convertedToday,
      driverPending
    });
  };

  const isPastTime = (timeStr) => {
    if (!timeStr) return false;
    const now = new Date();
    const endTime = timeStr.split(' - ')[1];
    if (endTime) {
      const [time, period] = endTime.trim().split(' ');
      const [hourStr] = time.split(':');
      let hour = parseInt(hourStr);
      if (period === 'PM' && hour !== 12) hour += 12;
      if (period === 'AM' && hour === 12) hour = 0;
      return now.getHours() > hour;
    }
    return false;
  };

  const processJobStats = (jobs) => {
    let carsInGarage = 0, waitingEstimate = 0, waitingApproval = 0;
    let waitingParts = 0, readyDelivery = 0, delivered = 0;
    
    const today = format(new Date(), 'yyyy-MM-dd');

    jobs.forEach(job => {
      const status = job.status?.toLowerCase();
      
      // Count delivered today only
      if (['delivered'].includes(status)) {
        const deliveredDate = job.delivered_at || job.updated_at;
        if (deliveredDate && deliveredDate.startsWith(today)) {
          delivered++;
        }
        return; // Skip further processing for delivered jobs
      }
      
      // Skip cancelled and dropped jobs
      if (['cancelled', 'dropped'].includes(status)) {
        return;
      }
      
      // All non-delivered/cancelled/dropped jobs are "in garage"
      carsInGarage++;
      
      // Waiting for estimate - car in garage but estimate not raised
      if (['draft', 'pending_estimate', 'reached_garage', 'picked_up'].includes(status)) {
        waitingEstimate++;
      }
      
      // Waiting for approval - estimate sent, customer approval pending
      if (['estimate_sent', 'quote_raised'].includes(status)) {
        waitingApproval++;
      }
      
      // Work yet to start - approved but work not started/in progress
      if (['customer_approved', 'quote_approved', 'customer_partial_approved', 'ongoing'].includes(status)) {
        waitingParts++;
      }
      
      // Ready for delivery
      if (['ready_for_delivery', 'invoiced', 'drop_scheduled'].includes(status)) {
        readyDelivery++;
      }
    });

    setJobStats({
      carsInGarage,
      waitingEstimate,
      waitingApproval,
      waitingParts,
      readyDelivery,
      delivered
    });
  };

  const fetchRevenueData = async () => {
    const token = localStorage.getItem('garage_token');
    const headers = { 'Authorization': `Bearer ${token}` };
    
    try {
      const { from, to } = getRevenueDateRange();
      const params = `from_date=${from.toISOString()}&to_date=${to.toISOString()}`;
      
      const res = await fetch(`${API_URL}/api/garage/dashboard/stats?${params}`, { headers });
      const data = await res.json();
      
      if (data.success && data.stats) {
        setRevenueStats({
          total: data.stats.total_revenue || 0,
          parts: data.stats.parts_revenue || 0,
          labor: data.stats.labor_revenue || 0,
          bodyShopLabor: data.stats.body_shop_revenue || 0
        });
      }
    } catch (error) {
      console.error('Failed to fetch revenue:', error);
    }
  };

  const getRevenueDateRange = () => {
    const now = new Date();
    switch (revenueFilter) {
      case 'today':
        return { from: startOfDay(now), to: endOfDay(now) };
      case 'yesterday':
        const yesterday = subDays(now, 1);
        return { from: startOfDay(yesterday), to: endOfDay(yesterday) };
      case 'week':
        return { from: startOfWeek(now, { weekStartsOn: 1 }), to: endOfWeek(now, { weekStartsOn: 1 }) };
      case 'mtd':
        return { from: startOfMonth(now), to: now };
      default:
        return { from: startOfDay(now), to: endOfDay(now) };
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const conversionRate = bookingStats.todayTotal > 0 
    ? Math.round((bookingStats.convertedToday / bookingStats.todayTotal) * 100) 
    : 0;

  if (loading) {
    return (
      <div className={`min-h-screen ${theme.pageBg} flex items-center justify-center`}>
        <div className="animate-spin w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${theme.pageBg} transition-colors duration-200 overflow-x-hidden`}>
      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Welcome Header */}
        <div className={`${theme.cardBg} rounded-xl p-4 border ${theme.border}`}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div>
              <h2 className={`text-xl font-bold ${theme.text}`}>Welcome, {garage?.name || 'Garage'}</h2>
              <p className={`text-sm ${theme.textMuted}`}>{format(new Date(), 'EEEE, MMMM d, yyyy')}</p>
            </div>
            <Button 
              onClick={() => navigate('/garage/jobs/new')}
              className="bg-orange-500 hover:bg-orange-600"
            >
              <Plus className="w-4 h-4 mr-2" /> New Job
            </Button>
          </div>
        </div>

        {/* BOOKINGS SECTION */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className={`text-lg font-semibold ${theme.text} flex items-center gap-2`}>
              <CalendarCheck className="w-5 h-5 text-orange-500" />
              Bookings
            </h3>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate('/garage/bookings')}
              className="text-orange-400 hover:text-orange-300"
            >
              View All <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          
          {/* Today & Tomorrow Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
            {/* Walkins Today */}
            <Card className={`${theme.cardBg} border ${theme.border} cursor-pointer hover:border-blue-500 transition-colors`}
                  onClick={() => navigate('/garage/bookings')}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <Users className="w-5 h-5 text-blue-500" />
                  <span className="text-2xl font-bold text-blue-500">{bookingStats.walkinsToday}</span>
                </div>
                <p className={`text-xs ${theme.textMuted} mt-1`}>Walk-ins Today</p>
              </CardContent>
            </Card>

            {/* Pickups Today */}
            <Card className={`${theme.cardBg} border ${theme.border} cursor-pointer hover:border-green-500 transition-colors`}
                  onClick={() => navigate('/garage/bookings')}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <Truck className="w-5 h-5 text-green-500" />
                  <span className="text-2xl font-bold text-green-500">{bookingStats.pickupsToday}</span>
                </div>
                <p className={`text-xs ${theme.textMuted} mt-1`}>Pickups Today</p>
              </CardContent>
            </Card>

            {/* Missed Yesterday */}
            <Card className={`${theme.cardBg} border ${theme.border} cursor-pointer hover:border-red-500 transition-colors`}
                  onClick={() => navigate('/garage/bookings')}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <CalendarX className="w-5 h-5 text-red-400" />
                  <span className="text-2xl font-bold text-red-400">{bookingStats.missedYesterday}</span>
                </div>
                <p className={`text-xs ${theme.textMuted} mt-1`}>Missed Yesterday</p>
              </CardContent>
            </Card>

            {/* Missed Today */}
            <Card className={`${theme.cardBg} border ${theme.border} cursor-pointer hover:border-red-500 transition-colors`}
                  onClick={() => navigate('/garage/bookings')}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <AlertCircle className="w-5 h-5 text-red-500" />
                  <span className="text-2xl font-bold text-red-500">{bookingStats.missedToday}</span>
                </div>
                <p className={`text-xs ${theme.textMuted} mt-1`}>Missed Today</p>
              </CardContent>
            </Card>

            {/* Walkins Tomorrow */}
            <Card className={`${theme.cardBg} border ${theme.border} cursor-pointer hover:border-cyan-500 transition-colors`}
                  onClick={() => navigate('/garage/bookings')}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <ArrowUpRight className="w-5 h-5 text-cyan-500" />
                  <span className="text-2xl font-bold text-cyan-500">{bookingStats.walkinsTomorrow}</span>
                </div>
                <p className={`text-xs ${theme.textMuted} mt-1`}>Walk-ins Tomorrow</p>
              </CardContent>
            </Card>

            {/* Pickups Tomorrow */}
            <Card className={`${theme.cardBg} border ${theme.border} cursor-pointer hover:border-emerald-500 transition-colors`}
                  onClick={() => navigate('/garage/bookings')}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <ArrowDownRight className="w-5 h-5 text-emerald-500" />
                  <span className="text-2xl font-bold text-emerald-500">{bookingStats.pickupsTomorrow}</span>
                </div>
                <p className={`text-xs ${theme.textMuted} mt-1`}>Pickups Tomorrow</p>
              </CardContent>
            </Card>

            {/* Driver Assignment Pending */}
            <Card className={`${theme.cardBg} border ${theme.border} cursor-pointer hover:border-yellow-500 transition-colors`}
                  onClick={() => navigate('/garage/bookings')}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <Truck className="w-5 h-5 text-yellow-500" />
                  <span className="text-2xl font-bold text-yellow-500">{bookingStats.driverPending}</span>
                </div>
                <p className={`text-xs ${theme.textMuted} mt-1`}>Driver Pending</p>
              </CardContent>
            </Card>
          </div>

          {/* Conversion Rate */}
          <Card className={`${theme.cardBg} border ${theme.border}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className={`text-sm ${theme.textMuted}`}>Today's Booking to Job Conversion</span>
                <span className={`text-lg font-bold ${conversionRate >= 70 ? 'text-green-500' : conversionRate >= 40 ? 'text-yellow-500' : 'text-red-500'}`}>
                  {conversionRate}%
                </span>
              </div>
              <Progress value={conversionRate} className="h-2" />
              <div className="flex justify-between mt-2 text-xs">
                <span className={theme.textMuted}>{bookingStats.convertedToday} converted</span>
                <span className={theme.textMuted}>{bookingStats.todayTotal} total bookings</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* JOBS SECTION */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className={`text-lg font-semibold ${theme.text} flex items-center gap-2`}>
              <Wrench className="w-5 h-5 text-orange-500" />
              Jobs
            </h3>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate('/garage/jobs')}
              className="text-orange-400 hover:text-orange-300"
            >
              View All <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
            {/* Cars in Garage */}
            <Card className={`${theme.cardBg} border ${theme.border} cursor-pointer hover:border-purple-500 transition-colors`}
                  onClick={() => navigate('/garage/jobs')}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <Car className="w-5 h-5 text-purple-500" />
                  <span className="text-2xl font-bold text-purple-500">{jobStats.carsInGarage}</span>
                </div>
                <p className={`text-xs ${theme.textMuted} mt-1`}>Cars in Garage</p>
              </CardContent>
            </Card>

            {/* Waiting Estimate */}
            <Card className={`${theme.cardBg} border ${theme.border} cursor-pointer hover:border-yellow-500 transition-colors`}
                  onClick={() => navigate('/garage/jobs?status=pending_estimate')}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <ClipboardList className="w-5 h-5 text-yellow-500" />
                  <span className="text-2xl font-bold text-yellow-500">{jobStats.waitingEstimate}</span>
                </div>
                <p className={`text-xs ${theme.textMuted} mt-1`}>Waiting Estimate</p>
              </CardContent>
            </Card>

            {/* Waiting Approval */}
            <Card className={`${theme.cardBg} border ${theme.border} cursor-pointer hover:border-blue-500 transition-colors`}
                  onClick={() => navigate('/garage/jobs?status=estimate_sent')}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <Clock className="w-5 h-5 text-blue-500" />
                  <span className="text-2xl font-bold text-blue-500">{jobStats.waitingApproval}</span>
                </div>
                <p className={`text-xs ${theme.textMuted} mt-1`}>Waiting Approval</p>
              </CardContent>
            </Card>

            {/* Waiting Parts / Work Yet to Start */}
            <Card className={`${theme.cardBg} border ${theme.border} cursor-pointer hover:border-orange-500 transition-colors`}
                  onClick={() => navigate('/garage/jobs?status=customer_approved')}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <Package className="w-5 h-5 text-orange-500" />
                  <span className="text-2xl font-bold text-orange-500">{jobStats.waitingParts}</span>
                </div>
                <p className={`text-xs ${theme.textMuted} mt-1`}>Work Yet to Start</p>
              </CardContent>
            </Card>

            {/* Ready for Delivery */}
            <Card className={`${theme.cardBg} border ${theme.border} cursor-pointer hover:border-green-500 transition-colors`}
                  onClick={() => navigate('/garage/jobs?status=ready_for_delivery')}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                  <span className="text-2xl font-bold text-green-500">{jobStats.readyDelivery}</span>
                </div>
                <p className={`text-xs ${theme.textMuted} mt-1`}>Ready for Delivery</p>
              </CardContent>
            </Card>

            {/* Delivered Today */}
            <Card className={`${theme.cardBg} border ${theme.border} cursor-pointer hover:border-emerald-500 transition-colors`}
                  onClick={() => navigate('/garage/jobs?status=delivered')}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <TrendingUp className="w-5 h-5 text-emerald-500" />
                  <span className="text-2xl font-bold text-emerald-500">{jobStats.delivered}</span>
                </div>
                <p className={`text-xs ${theme.textMuted} mt-1`}>Delivered Today</p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* REVENUE SECTION */}
        <div className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h3 className={`text-lg font-semibold ${theme.text} flex items-center gap-2`}>
              <IndianRupee className="w-5 h-5 text-orange-500" />
              Revenue
            </h3>
            <div className="flex items-center gap-1 bg-slate-800/50 rounded-lg p-1">
              {[
                { key: 'today', label: 'Today' },
                { key: 'yesterday', label: 'Yesterday' },
                { key: 'week', label: 'This Week' },
                { key: 'mtd', label: 'MTD' }
              ].map(({ key, label }) => (
                <Button
                  key={key}
                  variant={revenueFilter === key ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setRevenueFilter(key)}
                  className={revenueFilter === key ? 'bg-orange-500 hover:bg-orange-600' : ''}
                >
                  {label}
                </Button>
              ))}
            </div>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {/* Total Revenue */}
            <Card className={`${theme.cardBg} border ${theme.border} bg-gradient-to-br from-orange-500/10 to-transparent`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <IndianRupee className="w-6 h-6 text-orange-500" />
                  <Badge className="bg-orange-500/20 text-orange-400 border-0">
                    {revenueFilter === 'today' ? 'Today' : revenueFilter === 'yesterday' ? 'Yesterday' : revenueFilter === 'week' ? 'Week' : 'MTD'}
                  </Badge>
                </div>
                <p className="text-3xl font-bold text-orange-500">{formatCurrency(revenueStats.total)}</p>
                <p className={`text-xs ${theme.textMuted} mt-1`}>Total Revenue</p>
              </CardContent>
            </Card>

            {/* Parts Revenue */}
            <Card className={`${theme.cardBg} border ${theme.border}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <Package className="w-5 h-5 text-green-500" />
                </div>
                <p className="text-2xl font-bold text-green-500">{formatCurrency(revenueStats.parts)}</p>
                <p className={`text-xs ${theme.textMuted} mt-1`}>Parts</p>
              </CardContent>
            </Card>

            {/* Labor Revenue */}
            <Card className={`${theme.cardBg} border ${theme.border}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <Hammer className="w-5 h-5 text-blue-500" />
                </div>
                <p className="text-2xl font-bold text-blue-500">{formatCurrency(revenueStats.labor)}</p>
                <p className={`text-xs ${theme.textMuted} mt-1`}>Labor</p>
              </CardContent>
            </Card>

            {/* Body Shop Labor */}
            <Card className={`${theme.cardBg} border ${theme.border}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <PaintBucket className="w-5 h-5 text-purple-500" />
                </div>
                <p className="text-2xl font-bold text-purple-500">{formatCurrency(revenueStats.bodyShopLabor)}</p>
                <p className={`text-xs ${theme.textMuted} mt-1`}>Body Shop Labor</p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Button 
            onClick={() => navigate('/garage/bookings')}
            className={`h-auto py-4 ${theme.cardBg} border ${theme.border} hover:border-orange-500 flex flex-col items-center gap-2`}
            variant="ghost"
          >
            <CalendarCheck className="w-6 h-6 text-orange-500" />
            <span className={theme.text}>Bookings</span>
          </Button>
          <Button 
            onClick={() => navigate('/garage/jobs')}
            className={`h-auto py-4 ${theme.cardBg} border ${theme.border} hover:border-orange-500 flex flex-col items-center gap-2`}
            variant="ghost"
          >
            <ClipboardList className="w-6 h-6 text-blue-500" />
            <span className={theme.text}>All Jobs</span>
          </Button>
          <Button 
            onClick={() => navigate('/garage/invoices')}
            className={`h-auto py-4 ${theme.cardBg} border ${theme.border} hover:border-orange-500 flex flex-col items-center gap-2`}
            variant="ghost"
          >
            <FileText className="w-6 h-6 text-green-500" />
            <span className={theme.text}>Invoices</span>
          </Button>
          <Button 
            onClick={() => navigate('/garage/drivers')}
            className={`h-auto py-4 ${theme.cardBg} border ${theme.border} hover:border-orange-500 flex flex-col items-center gap-2`}
            variant="ghost"
          >
            <Truck className="w-6 h-6 text-purple-500" />
            <span className={theme.text}>Drivers</span>
          </Button>
        </div>
      </main>
    </div>
  );
};

export default GarageDashboard;
